import { IFilterViewModel } from 'infra/FiltersViewModel'
import { isNil } from 'lodash'
import { action, computed, observable, makeObservable } from 'mobx'
import { formatMoney } from 'utils/money-util'

export interface NumberRange {
  min: number | null
  max: number | null
}

export class NumberRangeFilter<TKey>
  implements IFilterViewModel<TKey, NumberRange>
{
  key: TKey
  title: string
  currencyCode: string | undefined
  @observable value: NumberRange | null = null
  @observable editing = false

  numberFormatter = new Intl.NumberFormat('en-US')

  constructor(key: TKey, title: string, currencyCode?: string) {
    makeObservable(this)
    this.key = key
    this.title = title
    this.currencyCode = currencyCode
  }

  @computed
  get valid() {
    return !isNil(this.value)
  }

  toString() {
    if (!this.value?.min && !this.value?.max) {
      return `${this.title}: No min - No max`
    }

    if (!this.value?.min) {
      return `${this.title} max: ${this.formatValue(this.value?.max ?? 0)}`
    }
    if (!this.value?.max) {
      return `${this.title} min: ${this.formatValue(this.value?.min ?? 0)}`
    }

    return `${this.title}: ${this.formatValue(
      this.value?.min,
    )} - ${this.formatValue(this.value?.max)}`
  }

  @action.bound
  setMin(val: number | null) {
    this.value = {
      min: val,
      max: this.value?.max ?? null,
    }
  }

  @action.bound
  setMax(val: number | null) {
    this.value = {
      min: this.value?.min ?? null,
      max: val,
    }
  }

  @action.bound
  clearValue() {
    this.value = null
  }

  @action.bound
  startEditing() {
    this.editing = true
  }

  @action.bound
  stopEditing() {
    this.editing = false
  }

  formatValue(value: number) {
    // We only support formatting as USD, but can change this if ever needed.
    return this.currencyCode
      ? formatMoney(value)
      : this.numberFormatter.format(value)
  }

  /**
   * ToDo: implement in order to support storing
   */
  serialize() {
    return null
  }

  /**
   * ToDo: implement in order to support storing
   */
  deserialize() {
    // not done yet
  }
}
